import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Course, Lesson, AdminLessonCreate, AdminLessonUpdate, LessonFile, FileUploadResponse } from '../../types';
import { adminAPI, coursesAPI } from '../../services/api';

interface LessonManagementProps {
  courses: Course[];
  selectedCourse: Course | null;
  onCourseSelected: (course: Course | null) => void;
  onCourseUpdated: (course: Course) => void;
}

interface SortableFileItemProps {
  file: LessonFile;
  onDelete: (fileId: number, lessonId: number) => void;
  onDownload: (fileId: number, filename: string) => void;
}

function SortableFileItem({ file, onDelete, onDownload }: SortableFileItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: file.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getFileIcon = (fileType: string) => {
    switch (fileType.toLowerCase()) {
      case 'pdf':
        return '📄';
      case 'doc':
      case 'docx':
        return '📝';
      case 'mp3':
      case 'wav':
        return '🎵';
      case 'mp4':
      case 'webm':
      case 'ogg':
        return '🎬';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'svg':
      case 'webp':
        return '🖼️';
      case 'md':
      case 'markdown':
        return '📝';
      default:
        return '📁';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between bg-gray-50 p-3 rounded border border-gray-200 hover:bg-gray-100 transition-colors"
    >
      <div className="flex items-center space-x-3 flex-1">
        {/* Drag Handle */}
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab hover:cursor-grabbing text-gray-400 hover:text-gray-600 p-1"
          title="Drag to reorder"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <span className="text-lg">{getFileIcon(file.file_type)}</span>
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900">
            {file.original_filename}
          </p>
          <p className="text-xs text-gray-500">
            {file.file_type.toUpperCase()} • {formatFileSize(file.file_size)}
          </p>
        </div>
      </div>

      <div className="flex items-center space-x-2">
        <button
          onClick={() => onDownload(file.id, file.original_filename)}
          className="text-blue-600 hover:text-blue-800 text-sm px-2 py-1 rounded hover:bg-blue-50 transition-colors"
          title="Download file"
        >
          ⬇️ Download
        </button>
        <button
          onClick={() => onDelete(file.id, file.lesson_id)}
          className="text-red-600 hover:text-red-800 text-sm px-2 py-1 rounded hover:bg-red-50 transition-colors"
        >
          Delete
        </button>
      </div>
    </div>
  );
}

const LessonManagement: React.FC<LessonManagementProps> = ({
  courses,
  selectedCourse,
  onCourseSelected,
  onCourseUpdated,
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState<Lesson | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [error, setError] = useState('');

  const [createFormData, setCreateFormData] = useState<AdminLessonCreate>({
    title: '',
    description: '',
    order: 1,
  });

  const [editFormData, setEditFormData] = useState<AdminLessonUpdate>({});

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (selectedLesson && showEditForm) {
      setEditFormData({
        title: selectedLesson.title,
        description: selectedLesson.description || '',
        order: selectedLesson.order,
      });
    }
  }, [selectedLesson, showEditForm]);

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCourse) return;

    setLoading(true);
    setError('');

    try {
      const newLesson = await adminAPI.createLesson(selectedCourse.id, createFormData);
      
      // Update the course with the new lesson
      const updatedCourse = {
        ...selectedCourse,
        lessons: [...selectedCourse.lessons, newLesson].sort((a, b) => a.order - b.order),
      };
      onCourseUpdated(updatedCourse);
      
      setShowCreateForm(false);
      setCreateFormData({
        title: '',
        description: '',
        order: selectedCourse.lessons.length + 1,
      });
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to create lesson');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLesson || !selectedCourse) return;

    setLoading(true);
    setError('');

    try {
      const updatedLesson = await adminAPI.updateLesson(selectedLesson.id, editFormData);
      
      // Update the course with the updated lesson
      const updatedCourse = {
        ...selectedCourse,
        lessons: selectedCourse.lessons.map(lesson =>
          lesson.id === updatedLesson.id ? updatedLesson : lesson
        ).sort((a, b) => a.order - b.order),
      };
      onCourseUpdated(updatedCourse);
      
      setShowEditForm(false);
      setSelectedLesson(null);
      setEditFormData({});
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to update lesson');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLesson = async (lessonId: number) => {
    if (!window.confirm('Are you sure you want to delete this lesson? This action cannot be undone.')) {
      return;
    }

    if (!selectedCourse) return;

    try {
      await adminAPI.deleteLesson(lessonId);
      
      // Update the course by removing the deleted lesson
      const updatedCourse = {
        ...selectedCourse,
        lessons: selectedCourse.lessons.filter(lesson => lesson.id !== lessonId),
      };
      onCourseUpdated(updatedCourse);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to delete lesson');
    }
  };

  const handleFileUpload = async (lessonId: number, file: File) => {
    setUploadLoading(true);
    setError('');

    try {
      const uploadResponse: FileUploadResponse = await adminAPI.uploadLessonFile(lessonId, file);
      
      // Create a new file object to add to the lesson
      const newFile: LessonFile = {
        id: uploadResponse.id,
        filename: uploadResponse.original_filename,
        original_filename: uploadResponse.original_filename,
        file_path: '', // This will be set by the backend
        file_type: uploadResponse.file_type,
        file_size: uploadResponse.file_size,
        lesson_id: lessonId,
        created_at: new Date().toISOString(),
      };

      // Update the course with the new file
      if (selectedCourse) {
        const updatedCourse = {
          ...selectedCourse,
          lessons: selectedCourse.lessons.map(lesson =>
            lesson.id === lessonId
              ? { ...lesson, files: [...(lesson.files || []), newFile] }
              : lesson
          ),
        };
        onCourseUpdated(updatedCourse);
      }
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDeleteFile = async (fileId: number, lessonId: number) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      await adminAPI.deleteLessonFile(fileId);
      
      // Update the course by removing the deleted file
      if (selectedCourse) {
        const updatedCourse = {
          ...selectedCourse,
          lessons: selectedCourse.lessons.map(lesson =>
            lesson.id === lessonId
              ? { ...lesson, files: (lesson.files || []).filter(file => file.id !== fileId) }
              : lesson
          ),
        };
        onCourseUpdated(updatedCourse);
      }
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to delete file');
    }
  };

  const handleDownloadFile = async (fileId: number, filename: string) => {
    try {
      const { signed_url } = await coursesAPI.getFileSignedUrl(fileId);
      
      // Create a temporary link to download the file
      const link = document.createElement('a');
      link.href = signed_url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to download file');
    }
  };

  const handleDragEnd = async (event: DragEndEvent, lessonId: number) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    if (!selectedCourse) return;

    const lesson = selectedCourse.lessons.find(l => l.id === lessonId);
    if (!lesson || !lesson.files) return;

    const oldIndex = lesson.files.findIndex(file => file.id === active.id);
    const newIndex = lesson.files.findIndex(file => file.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newFiles = arrayMove(lesson.files, oldIndex, newIndex);
    
    // Update the local state immediately for better UX
    const updatedCourse = {
      ...selectedCourse,
      lessons: selectedCourse.lessons.map(l =>
        l.id === lessonId
          ? { ...l, files: newFiles.map((file, index) => ({ ...file, order: index })) }
          : l
      ),
    };
    onCourseUpdated(updatedCourse);

    // Send the update to the backend
    try {
      const fileOrderUpdates = newFiles.map((file, index) => ({
        file_id: file.id,
        order: index
      }));
      
      await adminAPI.updateFileOrder(lessonId, fileOrderUpdates);
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to update file order');
      // Revert the change if the API call fails
      onCourseUpdated(selectedCourse);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Lesson Management</h2>
        {selectedCourse && (
          <button
            onClick={() => setShowCreateForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
          >
            Add New Lesson
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Course Selection */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Select Course</h3>
        <select
          value={selectedCourse?.id || ''}
          onChange={(e) => {
            const courseId = parseInt(e.target.value);
            const course = courses.find(c => c.id === courseId) || null;
            onCourseSelected(course);
          }}
          className="block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Select a course</option>
          {courses.map((course) => (
            <option key={course.id} value={course.id}>
              {course.title} ({course.lessons.length} lessons)
            </option>
          ))}
        </select>
      </div>

      {/* Lessons List */}
      {selectedCourse && (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Lessons for "{selectedCourse.title}"
            </h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              {selectedCourse.lessons.length} lesson(s)
            </p>
          </div>
          
          {selectedCourse.lessons.length === 0 ? (
            <div className="px-4 py-8 text-center text-gray-500">
              No lessons yet. Create your first lesson!
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {selectedCourse.lessons
                .sort((a, b) => a.order - b.order)
                .map((lesson) => (
                  <li key={lesson.id} className="px-4 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3">
                          <span className="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center text-sm font-medium">
                            {lesson.order}
                          </span>
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              {lesson.title}
                            </p>
                            {lesson.description && (
                              <p className="text-sm text-gray-500 truncate">
                                {lesson.description}
                              </p>
                            )}
                            <p className="text-xs text-gray-400 mt-1">
                              {lesson.files?.length || 0} file(s)
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedLesson(lesson);
                            setShowEditForm(true);
                          }}
                          className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded text-sm font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteLesson(lesson.id)}
                          className="bg-red-100 hover:bg-red-200 text-red-700 px-3 py-1 rounded text-sm font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    {/* Files Section */}
                    <div className="mt-4 ml-11">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium text-gray-700">Files</h4>
                        <label className="bg-green-100 hover:bg-green-200 text-green-700 px-3 py-1 rounded text-sm font-medium cursor-pointer">
                          Upload File
                          <input
                            type="file"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                handleFileUpload(lesson.id, file);
                              }
                            }}
                          />
                        </label>
                      </div>
                      
                      {uploadLoading && (
                        <div className="text-sm text-blue-600 mb-2">Uploading...</div>
                      )}

                      {!lesson.files || lesson.files.length === 0 ? (
                        <p className="text-sm text-gray-500">No files uploaded yet</p>
                      ) : (
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={(event) => handleDragEnd(event, lesson.id)}
                        >
                          <SortableContext
                            items={lesson.files.sort((a, b) => (a.order || 0) - (b.order || 0)).map(f => f.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="space-y-2">
                              {lesson.files
                                .sort((a, b) => (a.order || 0) - (b.order || 0))
                                .map((file) => (
                                  <SortableFileItem
                                    key={file.id}
                                    file={file}
                                    onDelete={handleDeleteFile}
                                    onDownload={handleDownloadFile}
                                  />
                                ))}
                            </div>
                          </SortableContext>
                        </DndContext>
                      )}
                    </div>
                  </li>
                ))}
            </ul>
          )}
        </div>
      )}

      {/* Create Lesson Modal */}
      {showCreateForm && selectedCourse && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Add Lesson to "{selectedCourse.title}"
              </h3>
              <form onSubmit={handleCreateSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <input
                    type="text"
                    required
                    value={createFormData.title}
                    onChange={(e) => setCreateFormData({ ...createFormData, title: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={createFormData.description}
                    onChange={(e) => setCreateFormData({ ...createFormData, description: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Order</label>
                  <input
                    type="number"
                    min="1"
                    required
                    value={createFormData.order}
                    onChange={(e) => setCreateFormData({ ...createFormData, order: parseInt(e.target.value) })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
                  >
                    {loading ? 'Creating...' : 'Create Lesson'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Lesson Modal */}
      {showEditForm && selectedLesson && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Lesson</h3>
              <form onSubmit={handleEditSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <input
                    type="text"
                    value={editFormData.title || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={editFormData.description || ''}
                    onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows={3}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Order</label>
                  <input
                    type="number"
                    min="1"
                    value={editFormData.order || 1}
                    onChange={(e) => setEditFormData({ ...editFormData, order: parseInt(e.target.value) })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditForm(false);
                      setSelectedLesson(null);
                    }}
                    className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-50"
                  >
                    {loading ? 'Updating...' : 'Update Lesson'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LessonManagement; 